import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { Button, IconButton } from '@mui/material';
import config from 'config';
import StarIcon from 'public/icons/icn-star.svg';
import StarHalfIcon from 'public/icons/icn-star-half.svg';
import LogoIcon from 'public/logo-rect.svg';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import useGlobalContext from '../../hooks/useGlobalContext';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import useMobilePlatform from '../../hooks/usePlatform';
import { GTM, GTMButtons, GTMLocations } from '../../utils/analytics';
import { redirectTo } from '../../utils/route-helpers';
import { useIsScreenSizeMax } from '../../utils/screen-size';

const STORAGE_KEY = 'app_download_banner_hidden';
const HIDE_DURATION_MS = 1000 * 60 * 60 * 24 * 7; // 7 days.

export const DownloadAppBanner = () => {
  const { t } = useTranslation();
  const { get: getFromLocalStorage, set: setToLocalStorage, clear: removeFromLocalStorage } = useLocalStorage();
  const { get: getFromContext, set: setToContext } = useGlobalContext();
  const isMobile = useIsScreenSizeMax('sm');
  const mobilePlatform = useMobilePlatform();

  const [isDownloadAppBannerHidden, setDownloadAppBannerHidden] = [
    getFromContext('isDownloadAppBannerHidden') as boolean,
    (isDownloadAppBannerHidden) => setToContext({ isDownloadAppBannerHidden }),
  ];

  const downloadAppUrl = config.downloadApp[mobilePlatform];

  const onClickHide = () => {
    setToLocalStorage(STORAGE_KEY, new Date().getTime());
    setDownloadAppBannerHidden(true);
  };

  const onClickDownload = () => {
    GTM.buttonClicked(GTMButtons.DOWNLOAD_APP_BUTTON, GTMLocations.APP_DOWNLOAD_BANNER);
    redirectTo({ href: downloadAppUrl });
  };

  useEffect(() => {
    const localStorageHidden = getFromLocalStorage(STORAGE_KEY);
    const hiddenAt = localStorageHidden ? +localStorageHidden : null;
    const isHiddenExpired = hiddenAt ? new Date().getTime() - hiddenAt > HIDE_DURATION_MS : true;

    if (hiddenAt && isHiddenExpired) {
      removeFromLocalStorage(STORAGE_KEY);
    }

    const hidden = !isMobile || !isHiddenExpired || !downloadAppUrl || !config.downloadApp.enabled;

    setDownloadAppBannerHidden(hidden);
  }, [isMobile, downloadAppUrl]);

  // Do not render if hidden.
  if (isDownloadAppBannerHidden) {
    return null;
  }

  return (
    <div className="download-app-banner">
      {/* Close button. */}
      <IconButton aria-label="close" className="download-app-close" onClick={onClickHide}>
        <CloseOutlinedIcon />
      </IconButton>

      {/* Logo. */}
      <LogoIcon className="download-app-logo" />

      {/* Text. */}
      <div className="download-app-info">
        <div className="download-app-title">
          Yaga
          <div className="download-app-rating">
            {new Array(4).fill(null).map((_, i) => (
              <StarIcon key={'star' + i} />
            ))}
            <StarHalfIcon />
          </div>
        </div>

        <div className="download-app-subtitle">
          <span>{t('components.app-download-banner.subtitle')}</span>
        </div>
      </div>

      {/* Cta. */}
      <Button variant="contained" onClick={onClickDownload} color="primary" size="medium" className="download-app-cta">
        {t('components.app-download-banner.cta')}
      </Button>
    </div>
  );
};
